/* eslint-disable react/jsx-no-bind */
import { FunctionalComponent, h, VNode } from '@stencil/core';
import clsx from 'clsx';
import { Configuration } from '../../../config/configuration';
import { t } from '../../i18n/i18n';
import { TargetGroup } from '../../utils/session-target-group-util';
import LanguageButton from './language-button';
import MenuButton from './menu-button';

type HeaderConfig = Pick<
  Configuration,
  | 'linkStartseite'
  | 'linkLeichteSprache'
  | 'linkGebaerdensprache'
  | 'linkEnglischeSprache'
  | 'linkPerspektiven'
  | 'linkOnlineTests'
  | 'linkWeiterbildungen'
  | 'linkFoerderung'
  | 'linkBeratung'
>;

type HeaderProps = {
  config: HeaderConfig;
  targetGroup: TargetGroup;
  logoSrc: string;
  location?: string;
  wide?: boolean;
  desktopHeaderRef?: (el: HTMLElement | undefined) => void;
  mobileHeaderRef?: (el: HTMLElement | undefined) => void;
  renderProfileButton?: (isMobile: boolean) => VNode;
};

const Header: FunctionalComponent<HeaderProps> = ({
  config: {
    linkStartseite,
    linkLeichteSprache,
    linkGebaerdensprache,
    linkEnglischeSprache,
    linkPerspektiven,
    linkOnlineTests,
    linkWeiterbildungen,
    linkFoerderung,
    linkBeratung,
  },
  targetGroup,
  logoSrc,
  location,
  wide,
  desktopHeaderRef,
  mobileHeaderRef,
  renderProfileButton,
}) => {
  const language = [
    // purge-css-from-tsx-exception dw-icon
    // purge-css-from-tsx-exception dw-icon-easy-read-fill
    { href: linkLeichteSprache, icon: 'easy-read-fill', label: t('header.language.leichte_sprache') },
    // purge-css-from-tsx-exception dw-icon-hand-fill
    { href: linkGebaerdensprache, icon: 'hand-fill', label: t('header.language.sign_language') },
    {
      href: linkEnglischeSprache,
      // purge-css-from-tsx-exception dw-icon-globe-fill
      icon: 'globe-fill',
      label: t('header.language.english_information'),
    },
  ];

  const menu = [
    {
      href: linkPerspektiven[targetGroup],
      label: t('header.menu.perspektiven'),
      patterns: [/privatpersonen\/berufe/iu, /unternehmen\/branchentrends/iu],
    },
    {
      href: linkOnlineTests[targetGroup],
      label: t('header.menu.online_tests'),
      patterns: [/privatpersonen\/online-tests/iu, /unternehmen\/online-tests/iu, /\/new-plan\//iu, /\/new-plan$/iu],
    },
    {
      href: linkWeiterbildungen[targetGroup],
      label: t('header.menu.weiterbildungen'),
      patterns: [
        /privatpersonen\/weiterbildungen/iu,
        /unternehmen\/weiterbildungen/iu,
        /weiterbildungssuche/iu,
        /now-suche/iu,
      ],
    },
    {
      href: linkFoerderung[targetGroup],
      label: t({
        id: 'header.menu.foerderung',
      }),
      patterns: [/privatpersonen\/foerderungen/iu, /unternehmen\/foerderungen/iu],
    },
    {
      href: linkBeratung[targetGroup],
      label: t('header.menu.beratung'),
      patterns: [/privatpersonen\/beratung/iu, /unternehmen\/beratung/iu],
    },
  ];

  const logo = (
    <div class="now-logo">
      <a href={linkStartseite} title={t('header.home_link_title')}>
        <img src={logoSrc} alt={t('header.logo_alt_text')} />
      </a>
    </div>
  );

  const languageKeyUp = (ev: KeyboardEvent) => {
    if (ev.key === 'Tab') {
      (ev.target as HTMLElement).scrollIntoView();
    }
  };

  return [
    <header
      key="desktop"
      class={clsx('now-header', { 'now-header-wide': wide, initializing: !desktopHeaderRef })}
      ref={desktopHeaderRef}
    >
      <nav class="now-nav-meta" aria-label={t('header.meta_navigation_label')}>
        <ul class="container-fluid">
          {language.map(({ label, href, icon }) => (
            <li key={href}>
              <a class={`now-link-on-dark now-icon dw-icon dw-icon-${icon}`} href={href} onKeyUp={languageKeyUp}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <nav class="now-nav-main" aria-label={t('header.main_navigation_label')}>
        <ul class="container-fluid">
          <li>{logo}</li>
          {menu.map(({ label, href, patterns }) => {
            const isActive = location && patterns.some((regex) => regex.test(location));
            return (
              <li
                key={href}
                class={clsx('now-menu-item d-none d-lg-block', {
                  active: isActive,
                })}
                aria-current={isActive ? 'page' : undefined}
              >
                <a href={href}>{label}</a>
              </li>
            );
          })}
          <li class="d-lg-none ms-auto">
            <MenuButton isMobile={false} entries={menu} />
          </li>
          {renderProfileButton?.(false)}
        </ul>
      </nav>
    </header>,
    <header key="mobile" class={clsx('now-mobile-header', { initializing: !mobileHeaderRef })} ref={mobileHeaderRef}>
      <div class="container-fluid fixed-bottom w-100">
        <LanguageButton entries={language} />
        <MenuButton isMobile={true} entries={[{ href: linkStartseite, label: t('header.menu.homepage') }, ...menu]} />
        {renderProfileButton?.(true)}
      </div>
      <div class="container-fluid">{logo}</div>
    </header>,
  ];
};

export default Header;
